/**
 * The Inbox nav item (tracker item 56): whether the signed-in user may use
 * the inbox under the company's inboxAccess setting, and their unread count
 * for the badge. ADMIN and OFFICE_MANAGER always see it; a TECHNICIAN only
 * when the setting is EVERYONE (read from GET /company-settings, open to every
 * role); nobody else. The count is refreshed on navigation and every minute.
 * A failed call hides the badge (and, for a technician, the item): the
 * server's gate is the real check.
 */
import { useEffect, useMemo, useState } from 'react';
import { createAPIClient, ICompanySettingsApi, IConversationsApi } from '../../api';
import * as types from '../../api/types';
import { inboxAllowed } from './inboxUi';

const REFRESH_MS = 60_000;

/** Fired by the inbox after reading or replying, so the badge updates at once. */
export const INBOX_UNREAD_CHANGED = 'crm:inbox-unread-changed';

export function notifyInboxUnreadChanged(): void {
  try {
    window.dispatchEvent(new Event(INBOX_UNREAD_CHANGED));
  } catch {
    // Non-essential
  }
}

export function useInboxNav(user: types.User | null, pathname: string): { visible: boolean; unread: number } {
  const api = useMemo<IConversationsApi & ICompanySettingsApi>(() => createAPIClient(), []);
  const role = user?.role;
  const [access, setAccess] = useState<types.InboxAccess | undefined>(undefined);
  const [unread, setUnread] = useState(0);

  // Only a technician's visibility depends on the setting
  useEffect(() => {
    if (role !== types.UserRole.TECHNICIAN) return undefined;
    let active = true;
    void (async () => {
      try {
        const settings = await api.getCompanySettings();
        if (active) setAccess(settings.inboxAccess ?? 'STAFF');
      } catch {
        if (active) setAccess('STAFF');
      }
    })();
    return () => { active = false; };
  }, [api, role]);

  const visible = inboxAllowed(role, access);

  useEffect(() => {
    if (!visible) {
      setUnread(0);
      return undefined;
    }
    let active = true;
    const load = async () => {
      try {
        const counts = await api.getConversationUnreadCount();
        if (active) setUnread(counts.total);
      } catch {
        if (active) setUnread(0);
      }
    };
    void load();
    const timer = window.setInterval(() => { void load(); }, REFRESH_MS);
    const onChanged = () => { void load(); };
    window.addEventListener(INBOX_UNREAD_CHANGED, onChanged);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener(INBOX_UNREAD_CHANGED, onChanged);
    };
  }, [api, visible, pathname]);

  return { visible, unread };
}
